import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { Button } from '../ui/button';
import { MessageSquare, ChevronLeft, RefreshCw, ExternalLink, Lock, Rocket } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { VaultModal } from './VaultModal';
import { useParams } from 'react-router-dom';
import { ConversationSidebar } from './ConversationSidebar';
import { ErrorListMinimal } from '../Errors/ErrorListMinimal';
import { VibeText } from './VibeText';
import { useNavbar } from '../../contexts/NavbarContext';
import toast, { Toaster } from 'react-hot-toast';

export interface StreamEvent {
  type: string;
  role?: string;
  id?: string;
  messageId?: string;
  delta?: string;
  error?: string;
  errorType?: string;  // Type of error (e.g., "internal", "ratelimited", "overloaded")
  toolCallId?: string;
  toolName?: string;
  inputTextDelta?: string;
  input?: any;  // Alternative field name used by backend
  toolInput?: any;
  output?: any;  // Alternative field name used by backend
  toolOutput?: any;
  toolIsError?: boolean;
  data?: any;
  url?: string;
  mediaType?: string;
}

interface TokenUsage {
  inputTokens?: number;
  outputTokens?: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  totalTokens?: number;
}

interface Conversation {
  id: string;
  title: string;
  created: string;
  updated: string;
  last_message_at?: string;
  active_model?: string;
}

// BlockStore types for stateful message accumulation
interface Block {
  id: string;
  type: 'text' | 'reasoning' | 'tool' | 'error';
  role?: 'user' | 'assistant';
  content: string;
  isComplete: boolean;
  isStreaming?: boolean; // For incomplete tool blocks (e.g., shell_exec)
  // For tool blocks
  toolCallId?: string;
  toolName?: string;
  toolInput?: any;
  toolOutput?: any;
  toolIsError?: boolean;
  // For reasoning blocks
  reasoningContent?: string;
}

interface PendingToolCall {
  toolCallId: string;
  toolName?: string;
  toolInput?: any;
  inputEventId?: string;  // Store the input event ID for proper sorting
}

interface BlockStore {
  blocks: Map<string, Block>;
  pendingToolCalls: Map<string, PendingToolCall>;
  latestError?: {
    id: string;
    content: string;
    errorType?: string;
  };
}

const PREVIEW_BASE_WIDTH = 1024;
const PREVIEW_BASE_HEIGHT = 768;

export const AppEditor: React.FC = () => {
  const [messages, setMessages] = useState<StreamEvent[]>([]);
  const [blockStore, setBlockStore] = useState<BlockStore>({
    blocks: new Map(),
    pendingToolCalls: new Map()
  });
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const [isAgentRunning, setIsAgentRunning] = useState(false);
  const [isInterrupting, setIsInterrupting] = useState(false);
  const [conversationId, setConversationId] = useState<string>('');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [isConversationSidebarExpanded, setIsConversationSidebarExpanded] = useState(false);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [application, setApplication] = useState<any>(null);
  const [availablePreviewWidth, setAvailablePreviewWidth] = useState(PREVIEW_BASE_WIDTH);
  const isMessagesVisible = true;
  const [models, setModels] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('claude-haiku-4-5');
  const [tokenUsage, setTokenUsage] = useState<TokenUsage | null>(null);
  const [stagedMessages, setStagedMessages] = useState<string[]>([]);
  const [failedStagedMessages, setFailedStagedMessages] = useState<string>('');
  const [isVaultModalOpen, setIsVaultModalOpen] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [isErrorsExpanded, setIsErrorsExpanded] = useState(false);
  const [selectedErrorId, setSelectedErrorId] = useState<number | null>(null);
  const [isDeploying, setIsDeploying] = useState<boolean>(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const localUserMessageRef = useRef<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const scrollPositionsRef = useRef<Map<string, number>>(new Map());
  const closedBlocksRef = useRef<Set<string>>(new Set()); // Track closed/finished blocks
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const resizeRafRef = useRef<number | null>(null);
  const latestWidthRef = useRef<number>(PREVIEW_BASE_WIDTH);
  const isAutoScrollingRef = useRef<boolean>(true); // Track if we should auto-scroll
  const isNewConversationRef = useRef<boolean>(false); // Track if this is a new conversation being created
  const stagedMessagesRef = useRef<string[]>([]); // Reference to staged messages for callbacks
  const stagedMessageControllersRef = useRef<Map<string, AbortController>>(new Map()); // Track abort controllers for staged messages
  const reconnectSSERef = useRef<(() => void) | null>(null); // Function to manually trigger SSE reconnection
  const processedEventIdsRef = useRef<Set<string>>(new Set()); // Track processed events to prevent duplicates
  const { getToken } = useAuth();
  const { id: appId } = useParams<{ id: string }>();
  const { setApplicationName } = useNavbar();

  const iframeScale = useMemo(() => {
    if (availablePreviewWidth <= 0) {
      return 0.01;
    }
    return Math.min(availablePreviewWidth / PREVIEW_BASE_WIDTH, 1);
  }, [availablePreviewWidth]);

  const scaledPreviewWidth = PREVIEW_BASE_WIDTH * iframeScale;
  const scaledPreviewHeight = PREVIEW_BASE_HEIGHT * iframeScale;

  // Keep stagedMessagesRef in sync with stagedMessages state
  useEffect(() => {
    stagedMessagesRef.current = stagedMessages;
  }, [stagedMessages]);

  // Handle ESC key for interrupt or cancel staged messages
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isAgentRunning && !isInterrupting) {
        event.preventDefault();

        // If there are staged messages, cancel them instead of interrupting
        if (stagedMessages.length > 0) {
          // Cancel all pending staged message requests
          stagedMessageControllersRef.current.forEach(controller => {
            controller.abort();
          });
          stagedMessageControllersRef.current.clear();

          // Move staged messages to input field
          stagedMessages.forEach(msg => addToFailedMessages(msg));
          setStagedMessages([]);
        } else {
          // No staged messages, interrupt the agent
          handleInterrupt();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAgentRunning, isInterrupting, stagedMessages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Collapse error details when sidebar is collapsed
  useEffect(() => {
    if (!isSidebarExpanded && isErrorsExpanded) {
      setIsErrorsExpanded(false);
      setSelectedErrorId(null);
    }
  }, [isSidebarExpanded, isErrorsExpanded]);

  // Keep the preview scaled to the available sidebar width
  useLayoutEffect(() => {
    const container = iframeContainerRef.current;
    if (!container) {
      return;
    }

    const scheduleWidthUpdate = (width: number) => {
      latestWidthRef.current = width;
      if (resizeRafRef.current !== null) {
        return;
      }

      resizeRafRef.current = window.requestAnimationFrame(() => {
        resizeRafRef.current = null;
        const nextWidth = latestWidthRef.current;
        setAvailablePreviewWidth(prev => {
          if (Math.abs(prev - nextWidth) < 0.5) {
            return prev;
          }
          return nextWidth;
        });
      });
    };

    const computeAvailableWidth = () => {
      const measuredWidth = container.clientWidth;
      if (measuredWidth <= 0) {
        return;
      }
      scheduleWidthUpdate(measuredWidth);
    };

    computeAvailableWidth();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(entries => {
        entries.forEach(entry => {
          const measuredWidth = entry.contentRect.width;
          if (measuredWidth > 0) {
            scheduleWidthUpdate(measuredWidth);
          }
        });
      });

      resizeObserver.observe(container);

      return () => {
        resizeObserver.disconnect();
        if (resizeRafRef.current !== null) {
          window.cancelAnimationFrame(resizeRafRef.current);
          resizeRafRef.current = null;
        }
      };
    }

    window.addEventListener('resize', computeAvailableWidth);
    return () => {
      window.removeEventListener('resize', computeAvailableWidth);
      if (resizeRafRef.current !== null) {
        window.cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
    };
  }, [application?.preview_url, isSidebarExpanded]);

  // SSE listener for rebuild events from watcher - DISABLED
  // useEffect(() => {
  //   if (!application?.preview_url) return;

  //   let eventSource: EventSource | null = null;
  //   let reconnectTimeout: NodeJS.Timeout;
  //   let reconnectAttempts = 0;

  //   const connect = () => {
  //     const url = `${application.preview_url}rebuild-events`;
  //     eventSource = new EventSource(url);

  //     eventSource.onopen = () => {
  //       reconnectAttempts = 0;
  //     };

  //     eventSource.onmessage = (event) => {
  //       try {
  //         const data = JSON.parse(event.data);

  //         if (data.type === 'rebuild-complete' && data.success) {
  //           // Small delay to ensure the new server is fully ready
  //           setTimeout(() => {
  //             reloadIframe();
  //           }, 500);
  //         }
  //       } catch (err) {
  //         // Silently ignore parse errors
  //       }
  //     };

  //     eventSource.onerror = () => {
  //       eventSource?.close();

  //       // Exponential backoff with jitter: base delay doubles each time, max 30s
  //       const baseDelay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
  //       // Add jitter: ±25% randomization
  //       const jitter = baseDelay * 0.25 * (Math.random() * 2 - 1);
  //       const delay = Math.round(baseDelay + jitter);
  //       reconnectAttempts++;

  //       console.log(`Rebuild SSE reconnect attempt ${reconnectAttempts} in ${delay}ms`);
  //       reconnectTimeout = setTimeout(connect, delay);
  //     };
  //   };

  //   connect();

  //   return () => {
  //     clearTimeout(reconnectTimeout);
  //     eventSource?.close();
  //   };
  // }, [application?.preview_url]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load saved model preference from localStorage
  useEffect(() => {
    const savedModel = localStorage.getItem('selectedModel');
    if (savedModel) {
      setSelectedModel(savedModel);
    }
  }, []);

  // Fetch available models
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const token = await getToken();
        const response = await fetch('/api/models', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setModels(data);
        }
      } catch (error) {
        console.error('Failed to fetch models:', error);
      }
    };

    fetchModels();
  }, [getToken]);


  // Fetch application data on mount
  useEffect(() => {
    const fetchApplication = async () => {
      if (!appId) return;

      try {
        const token = await getToken();
        const response = await fetch(`/api/applications/${appId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setApplication(data);
          // Set the application name in the navbar context
          if (data.name) {
            setApplicationName(data.name);
          }
        }
      } catch (error) {
        console.error('Failed to fetch application:', error);
      }
    };

    fetchApplication();

    // Clear the application name when component unmounts
    return () => {
      setApplicationName(null);
    };
  }, [appId, getToken, setApplicationName]);

  // Fetch conversations on mount and auto-select the most recent
  useEffect(() => {
    const fetchConversations = async () => {
      if (!appId) {
        setLoadingConversations(false);
        return;
      }

      setLoadingConversations(true);
      try {
        const token = await getToken();
        const response = await fetch(`/api/demo/applications/${appId}/conversations`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setConversations(data);

          // Auto-select the most recent conversation if none is selected
          // The conversations are already sorted by updated DESC from the backend
          if (data.length > 0 && !conversationId) {
            const mostRecent = data[0];
            setConversationId(mostRecent.id);
            // Set the model from the conversation if available
            if (mostRecent.active_model) {
              setSelectedModel(mostRecent.active_model);
            }
            // Don't need to set visibility here - the useEffect will handle it
          }
          // No else needed - if no conversations, area stays visible
        }
      } catch (error) {
        console.error('Failed to fetch conversations:', error);
      } finally {
        setLoadingConversations(false);
      }
    };

    fetchConversations();
  }, [appId, getToken]); // Remove conversationId from deps to avoid re-fetching when it changes

  // Setup listener when conversation starts
  useEffect(() => {
    if (!conversationId || !appId) return;

    // Reset the new conversation flag if it was set
    if (isNewConversationRef.current) {
      isNewConversationRef.current = false;
    }

    // Use direct backend URL to avoid proxy issues with EventSource
    const backendUrl = process.env.NODE_ENV === 'development'
      ? `http://localhost:8080/api/demo/applications/${appId}/listen?conversation_id=${conversationId}`
      : `/api/demo/applications/${appId}/listen?conversation_id=${conversationId}`;

    let reconnectTimeout: NodeJS.Timeout;
    let reconnectAttempts = 0;
    let shouldReconnect = true;

    const setupEventSource = async () => {
      try {
        const token = await getToken();
        // Note: EventSource doesn't support headers, so we pass token as query param
        // Clean up previous event source
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }

        const urlWithToken = `${backendUrl}&token=${encodeURIComponent(token || '')}`;
        const source = new EventSource(urlWithToken);
        eventSourceRef.current = source;

        source.onopen = () => {
          console.log('SSE connection opened');
          reconnectAttempts = 0; // Reset reconnect attempts on successful connection
        };

        source.onmessage = (event) => {
          try {
            const streamEvent: StreamEvent = JSON.parse(event.data);
            handleStreamEvent(streamEvent);
          } catch (error) {
            console.error('Failed to parse listener event:', error);
          }
        };

        source.onerror = (error) => {
          console.error('Listener connection error:', error);
          source.close();

          // Always reconnect if we should (not manually closed) - no limit on attempts
          if (shouldReconnect) {
            // Exponential backoff with jitter: base delay doubles each time, max 30s
            const baseDelay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
            // Add jitter: ±25% randomization
            const jitter = baseDelay * 0.25 * (Math.random() * 2 - 1);
            const delay = Math.round(baseDelay + jitter);

            console.log(`SSE reconnect attempt #${reconnectAttempts + 1} in ${delay}ms`);
            reconnectAttempts++;
            // Clear local user message ref on error to prevent stale state
            localUserMessageRef.current = null;

            reconnectTimeout = setTimeout(() => {
              if (shouldReconnect) {
                setupEventSource();
              }
            }, delay);
          }
        };
      } catch (error) {
        console.error('Failed to setup event source:', error);

        // Retry setup on failure - forever
        if (shouldReconnect) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
          console.log(`SSE setup retry #${reconnectAttempts + 1} in ${delay}ms`);
          reconnectAttempts++;
          reconnectTimeout = setTimeout(() => {
            if (shouldReconnect) {
              setupEventSource();
            }
          }, delay);
        }
      }
    };

    // Store the reconnect function so we can call it manually
    reconnectSSERef.current = () => {
      if (!eventSourceRef.current || eventSourceRef.current.readyState === EventSource.CLOSED) {
        console.log('Manually triggering SSE reconnection');
        clearTimeout(reconnectTimeout);
        reconnectAttempts = 0; // Reset attempts for manual reconnect
        // Clear local user message ref on reconnection to prevent stale state
        localUserMessageRef.current = null;
        setupEventSource();
      }
    };

    setupEventSource();
    return () => {
      shouldReconnect = false; // Stop reconnection attempts
      reconnectSSERef.current = null;
      clearTimeout(reconnectTimeout);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      // Restore scroll position if available
      if (scrollAreaRef.current && conversationId && scrollPositionsRef.current.has(conversationId)) {
        scrollAreaRef.current.scrollTop = scrollPositionsRef.current.get(conversationId) || 0;
      }
      // Reset input and agent states when EventSource is closed (conversation switch)
      // This ensures the new conversation starts with a clean state
      setIsInputDisabled(false);
      setIsAgentRunning(false);
      setIsInterrupting(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, appId, getToken]);

  // Helper function to strip system reminder tags from text content
  const stripReminderTags = (text: string): string => {
    return text
      .replace(/<system-reminder[^>]*>.*?<\/system-reminder>/gs, '')
      .replace(/<reminder[^>]*>.*?<\/reminder>/gs, '')
      .trim();
  };

  // Update BlockStore based on stream events
  const updateBlockStore = (store: BlockStore, event: StreamEvent): BlockStore => {
    const newStore: BlockStore = {
      blocks: new Map(store.blocks),
      pendingToolCalls: new Map(store.pendingToolCalls),
      latestError: store.latestError
    };

    // Handle error events - set as singleton latest error
    if (event.type === 'error') {
      newStore.latestError = {
        id: `error-${Date.now()}-${Math.random()}`,
        content: event.error || 'An unknown error occurred',
        errorType: event.errorType
      };
      return newStore;
    }

    // Clear any existing error when new non-error messages arrive
    // (except for start/finish/usage events which are meta events)
    if (event.type !== 'start' && event.type !== 'finish' && event.type !== 'data-usage' &&
      event.type !== 'start-step' && event.type !== 'finish-step' &&
      !event.type?.startsWith('data-')) {
      newStore.latestError = undefined;
    }

    // Handle text events
    if (event.type === 'text-start') {
      const blockId = event.id;
      if (!blockId) return store;

      // Skip if block already exists and is complete (replay protection)
      const existingBlock = store.blocks.get(blockId);
      if (existingBlock && existingBlock.isComplete) {
        return store;
      }

      const block: Block = {
        id: blockId,
        type: 'text',
        role: event.role === 'user' ? 'user' : 'assistant',
        content: '',
        isComplete: false
      };
      newStore.blocks.set(blockId, block);
    } else if (event.type === 'text-delta') {
      const blockId = event.id;
      if (!blockId) return store;

      const existingBlock = store.blocks.get(blockId);
      // Skip if block doesn't exist or is already complete (replay protection)
      if (!existingBlock || existingBlock.isComplete) {
        return store;
      }
      if (existingBlock.type === 'text') {
        // Create a new block object to avoid mutation
        const updatedBlock: Block = {
          ...existingBlock,
          content: existingBlock.content + (event.delta || '')
        };
        newStore.blocks.set(blockId, updatedBlock);
      }
    } else if (event.type === 'text-end') {
      const blockId = event.id;
      if (!blockId) return store;

      const existingBlock = store.blocks.get(blockId);
      // Skip if block doesn't exist or is already complete (idempotent)
      if (!existingBlock || existingBlock.isComplete) {
        return store;
      }
      // Create a new block object to avoid mutation
      const updatedBlock: Block = {
        ...existingBlock,
        isComplete: true,
        content: existingBlock.role === 'user' ? stripReminderTags(existingBlock.content) : existingBlock.content
      };
      newStore.blocks.set(blockId, updatedBlock);
    }

    // Handle reasoning events
    else if (event.type === 'reasoning-start') {
      const blockId = event.id;
      if (!blockId) return store;

      // Skip if block already exists and is complete (replay protection)
      const existingBlock = store.blocks.get(blockId);
      if (existingBlock && existingBlock.isComplete) {
        return store;
      }

      const block: Block = {
        id: blockId,
        type: 'reasoning',
        content: '',
        reasoningContent: '',
        isComplete: false
      };
      newStore.blocks.set(blockId, block);
    } else if (event.type === 'reasoning-delta') {
      const blockId = event.id;
      if (!blockId) return store;

      const existingBlock = store.blocks.get(blockId);
      // Skip if block doesn't exist or is already complete (replay protection)
      if (!existingBlock || existingBlock.isComplete) {
        return store;
      }
      if (existingBlock.type === 'reasoning') {
        // Create a new block object to avoid mutation
        const updatedBlock: Block = {
          ...existingBlock,
          reasoningContent: (existingBlock.reasoningContent || '') + (event.delta || '')
        };
        newStore.blocks.set(blockId, updatedBlock);
      }
    } else if (event.type === 'reasoning-end') {
      const blockId = event.id;
      if (!blockId) return store;

      const existingBlock = store.blocks.get(blockId);
      // Skip if block doesn't exist or is already complete (idempotent)
      if (!existingBlock || existingBlock.isComplete) {
        return store;
      }
      // Create a new block object to avoid mutation
      const updatedBlock: Block = {
        ...existingBlock,
        isComplete: true
      };
      newStore.blocks.set(blockId, updatedBlock);
    }

    // Handle tool events (most complex)
    else if (event.type === 'tool-input-start') {
      const mapKey = event.toolCallId;
      if (!mapKey) return store;

      // Skip if block already exists and is complete (replay protection)
      const existingBlock = store.blocks.get(mapKey);
      if (existingBlock && existingBlock.isComplete) {
        return store;
      }

      // Store pending tool call
      const pending: PendingToolCall = {
        toolCallId: event.toolCallId!,
        toolName: event.toolName
      };
      newStore.pendingToolCalls.set(event.toolCallId!, pending);
    } else if (event.type === 'tool-input-available') {
      // Use toolCallId as Map key for consistency, but event.id for block.id (UUIDv7 sorting)
      const mapKey = event.toolCallId;
      const blockIdForSorting = event.id || event.toolCallId;
      if (!mapKey || !blockIdForSorting) return store;

      // Skip if block already exists and is complete (replay protection)
      const existingBlock = store.blocks.get(mapKey);
      if (existingBlock && existingBlock.isComplete) {
        return store;
      }

      // Update pending tool with input - create new object
      const existingPending = store.pendingToolCalls.get(event.toolCallId!);
      if (existingPending) {
        const updatedPending: PendingToolCall = {
          ...existingPending,
          toolInput: event.toolInput || event.input,
          inputEventId: event.id  // Store input event ID for sorting
        };
        newStore.pendingToolCalls.set(event.toolCallId!, updatedPending);

        // For shell_exec, create incomplete block immediately
        if (existingPending.toolName === 'shell_exec') {
          const block: Block = {
            id: blockIdForSorting,  // UUIDv7 for sorting
            type: 'tool',
            content: '',
            toolCallId: event.toolCallId,
            toolName: existingPending.toolName,
            toolInput: event.toolInput || event.input,
            toolOutput: undefined,
            toolIsError: false,
            isComplete: false,
            isStreaming: true
          };
          newStore.blocks.set(mapKey, block);  // Use toolCallId as map key
        }
      }
    } else if (event.type === 'tool-output-available') {
      // Use toolCallId as Map key for consistency, but event.id for block.id (UUIDv7 sorting)
      const mapKey = event.toolCallId;
      const blockIdForSorting = event.id || event.toolCallId;
      if (!mapKey || !blockIdForSorting) return store;

      // Check if block already exists (for shell_exec)
      const existingBlock = store.blocks.get(mapKey);
      if (existingBlock && existingBlock.toolName === 'shell_exec') {
        // Skip if already complete (replay protection, but allow incomplete shell_exec to receive output)
        if (existingBlock.isComplete) {
          return store;
        }
        // Update existing shell_exec block with output
        const updatedBlock: Block = {
          ...existingBlock,
          toolOutput: event.toolOutput || event.output,
          toolIsError: event.toolIsError,
          isComplete: true,
          isStreaming: false
        };
        newStore.blocks.set(mapKey, updatedBlock);
        newStore.pendingToolCalls.delete(event.toolCallId!);
      } else {
        // Skip if block already complete (replay protection)
        if (existingBlock && existingBlock.isComplete) {
          return store;
        }

        // Create complete tool block for other tools
        const pending = newStore.pendingToolCalls.get(event.toolCallId!);
        if (pending) {
          const block: Block = {
            id: pending.inputEventId || event.id || event.toolCallId || '',  // Use input event ID for sorting
            type: 'tool',
            content: '',
            toolCallId: event.toolCallId,
            toolName: pending.toolName,
            toolInput: pending.toolInput,
            toolOutput: event.toolOutput || event.output,
            toolIsError: event.toolIsError,
            isComplete: true
          };
          newStore.blocks.set(mapKey, block);  // Use toolCallId as map key
          newStore.pendingToolCalls.delete(event.toolCallId!);
        }
      }
    }

    return newStore;
  };

  const handleStreamEvent = (streamEvent: StreamEvent) => {

    // Handle start/finish events for input control and agent running state
    if (streamEvent.type === 'start') {
      setIsInputDisabled(true);
      setIsAgentRunning(true);
      setIsInterrupting(false); // Reset interrupting state
    } else if (streamEvent.type === 'finish') {
      setIsInputDisabled(false);
      setIsAgentRunning(false);
      setIsInterrupting(false); // Reset interrupting state
      // Clear staged messages on successful completion
      setStagedMessages([]);
      setFailedStagedMessages('');
    }

    // Capture token usage data
    if (streamEvent.type === 'data-usage' && streamEvent.data) {
      setTokenUsage(streamEvent.data as TokenUsage);
    }

    // Log error events for debugging
    if (streamEvent.type === 'error') {
      console.error('Error event received:', streamEvent);
      // Also stop agent on error
      if (streamEvent.error?.includes('interrupt')) {
        setIsAgentRunning(false);
        setIsInterrupting(false);
      }
      // On error, move staged messages to input field
      if (stagedMessagesRef.current.length > 0) {
        stagedMessagesRef.current.forEach(msg => addToFailedMessages(msg));
        setStagedMessages([]);
      }
    }

    // Skip events for the user message we just sent locally
    // Only skip if the message ID matches exactly what we sent
    if (localUserMessageRef.current === streamEvent.id && streamEvent.role === 'user') {
      if (streamEvent.type === 'text-start' ||
        streamEvent.type === 'text-delta' ||
        streamEvent.type === 'text-end') {
        // This is a user message event from the backend, skip it
        // Clear the ref after we've seen the text-end event
        if (streamEvent.type === 'text-end') {
          localUserMessageRef.current = null;
        }
        return;
      }
    }

    // Deduplicate events using their ID (but NOT delta events)
    // Delta events are supposed to have the same ID - they build up a block incrementally
    const isDeltaEvent = streamEvent.type.includes('-delta');
    if (!isDeltaEvent) {
      const eventKey = `${streamEvent.type}-${streamEvent.id || streamEvent.toolCallId || streamEvent.messageId || Math.random()}`;
      if (processedEventIdsRef.current.has(eventKey)) {
        // Skip duplicate event
        return;
      }
      processedEventIdsRef.current.add(eventKey);
    }

    // Just add the event directly
    setMessages(prev => [...prev, streamEvent]);

    // Update block store with the event
    setBlockStore(prev => updateBlockStore(prev, streamEvent));

    // Auto-scroll to bottom only if we're attached (user hasn't scrolled up)
    if (isAutoScrollingRef.current) {
      setTimeout(() => {
        if (scrollAreaRef.current) {
          scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
        }
      }, 50);
    }
  };

  const handleScroll = () => {
    if (!scrollAreaRef.current) return;

    const element = scrollAreaRef.current;
    const threshold = 50; // pixels from bottom to consider "at bottom"
    const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < threshold;

    // Update auto-scrolling state
    isAutoScrollingRef.current = isAtBottom;

    // Save the current scroll position for this conversation
    if (conversationId) {
      scrollPositionsRef.current.set(conversationId, element.scrollTop);
    }
  };

  // Helper function to add message to failed messages without duplicating
  const addToFailedMessages = (messageToAdd: string) => {
    setFailedStagedMessages(existing => {
      // If the message is already in the failed messages, don't add it again
      if (existing && existing.includes(messageToAdd)) {
        return existing;
      }
      return existing ? `${messageToAdd}\n\n${existing}` : messageToAdd;
    });
  };

  const handleAskAgentToFix = async () => {
    const message = "Please review the error reports and fix them. Use the error_reports_list tool to get the full error details, then investigate and fix the issues.";
    await sendMessage(message, selectedModel);
  };

  const handleAskAgentToFixError = async (error: any) => {
    let message = `Please fix the following error:\n\n`;
    message += `Error ID: ${error.id}\n`;
    message += `Error Type: ${error.error_type}\n`;
    message += `Message: ${error.representative_message}\n`;
    message += `First seen: ${error.first_seen_at}\n`;
    message += `Last seen: ${error.last_seen_at}\n`;
    message += `Occurrences: ${error.occurrence_count}\n`;

    if (error.representative_stack && error.representative_stack.length > 0) {
      message += `\nStack trace:\n${error.representative_stack.join('\n')}`;
    }

    await sendMessage(message, selectedModel);
  };

  const handleErrorClick = (errorId: number) => {
    setSelectedErrorId(errorId);
    setIsErrorsExpanded(true);
    setIsSidebarExpanded(true);
  };

  const handleCloseErrorDetails = () => {
    setIsErrorsExpanded(false);
    setSelectedErrorId(null);
    setIsSidebarExpanded(false);
  };

  const sendMessage = async (message: string, model: string) => {
    if (!message.trim() || !appId) return;

    // If agent is running, stage the message instead
    if (isAgentRunning && conversationId) {
      // Immediately add to staged messages list for display
      setStagedMessages(prev => [...prev, message]);
      // Clear any failed staged messages since we're adding new ones
      setFailedStagedMessages('');

      // Call the backend API in the background with a long timeout
      getToken().then(token => {
        // Create an AbortController for timeout and cancellation
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

        // Store the controller so it can be cancelled with ESC
        stagedMessageControllersRef.current.set(message, controller);

        fetch(`/api/demo/applications/${appId}/stage-message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            message,
            conversation_id: conversationId
          }),
          signal: controller.signal
        }).then(async response => {
          clearTimeout(timeoutId);
          // Remove the controller now that the request is done
          stagedMessageControllersRef.current.delete(message);

          if (!response.ok) {
            console.error('Failed to stage message on backend');
            // On failure, move this message back to the input field
            setStagedMessages(prev => prev.filter(m => m !== message));
            addToFailedMessages(message);
          } else {
            // Parse the response to check if message was sent or unsent
            try {
              const data = await response.json();
              if (data.status === 'sent') {
                // Message was successfully sent - remove from staged list
                setStagedMessages(prev => prev.filter(m => m !== message));
              } else if (data.status === 'unsent') {
                // Message was not sent (agent still busy) - move back to input
                setStagedMessages(prev => prev.filter(m => m !== message));
                addToFailedMessages(message);
              }
            } catch (err) {
              console.error('Failed to parse stage response:', err);
            }
          }
        }).catch(error => {
          clearTimeout(timeoutId);
          // Remove the controller now that the request is done
          stagedMessageControllersRef.current.delete(message);

          if (error.name === 'AbortError') {
            // Check if this was a timeout or user cancellation
            if (stagedMessageControllersRef.current.has(message)) {
              console.log('Stage message request was cancelled by user');
            } else {
              console.error('Stage message request timed out after 30s');
            }
          } else {
            console.error('Error staging message:', error);
          }

          // Only move to input if it wasn't already handled by ESC key
          if (stagedMessagesRef.current.includes(message)) {
            setStagedMessages(prev => prev.filter(m => m !== message));
            addToFailedMessages(message);
          }
        });
      });

      return;
    }

    // Regular message sending (when agent is not running)
    if (isInputDisabled) return;

    // Set agent as running immediately for instant UI feedback
    setIsInputDisabled(true);
    setIsAgentRunning(true);

    // Re-attach auto-scrolling when sending a new message
    isAutoScrollingRef.current = true;

    // Add user message to display locally (won't come from stream)
    const messageId = `user-${Date.now()}`;
    localUserMessageRef.current = messageId; // Track that we've added a local user message

    const userMessageStart: StreamEvent = {
      type: 'text-start',
      role: 'user',
      id: messageId,
    };
    const userMessageDelta: StreamEvent = {
      type: 'text-delta',
      id: messageId,
      delta: message
    };
    const userMessageEnd: StreamEvent = {
      type: 'text-end',
      role: 'user',
      id: messageId,
    };
    setMessages(prev => [...prev, userMessageStart, userMessageDelta, userMessageEnd]);

    // Clear saved scroll position when sending a new message (we want to scroll to bottom)
    if (conversationId) {
      scrollPositionsRef.current.delete(conversationId);
    }

    // Scroll to bottom when sending a message
    setTimeout(() => {
      if (scrollAreaRef.current) {
        scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
      }
    }, 50);

    // Send the message and get the conversation ID
    try {
      const token = await getToken();
      const response = await fetch(`/api/demo/applications/${appId}/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message,
          model,
          conversation_id: conversationId || undefined // Include conversation ID if we have one
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      // Get the conversation data from the response
      const data = await response.json();

      // The response now contains the full conversation object
      const conversationData = data.id ? data : {
        id: data.conversation_id,
        title: data.title || 'New conversation',
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
        last_message_at: new Date().toISOString(),
        active_model: model
      };

      // Update or add the conversation in the list
      setConversations(prevConversations => {
        // Handle null or undefined prevConversations
        const conversations = prevConversations || [];
        const existingIndex = conversations.findIndex((conv: Conversation) => conv.id === conversationData.id);

        if (existingIndex >= 0) {
          // Update existing conversation
          const updated = [...conversations];
          updated[existingIndex] = conversationData;
          // Sort by last_message_at or updated to keep most recent at top
          updated.sort((a, b) => {
            const aTime = new Date(a.last_message_at || a.updated).getTime();
            const bTime = new Date(b.last_message_at || b.updated).getTime();
            return bTime - aTime;
          });
          return updated;
        } else {
          // Add new conversation at the top
          return [conversationData, ...conversations];
        }
      });

      // If this is a new conversation, update the conversation ID
      if (conversationData.id !== conversationId) {
        // Mark this as a new conversation we're creating
        isNewConversationRef.current = true;
        // This will trigger the useEffect to set up the listener
        setConversationId(conversationData.id);
      } else {
        // For existing conversation, check if SSE is connected
        // If not, manually trigger reconnection
        if (!eventSourceRef.current || eventSourceRef.current.readyState === EventSource.CLOSED) {
          console.log('SSE disconnected after message sent, reconnecting...');
          if (reconnectSSERef.current) {
            reconnectSSERef.current();
          }
        }
      }

      // Clear the local user message ref after successfully sending
      // This ensures we don't skip future user messages in the stream
      localUserMessageRef.current = null;
    } catch (error) {
      console.error('Failed to send message:', error);
      // Re-enable input on error since we won't receive a finish event
      setIsInputDisabled(false);
      // Also clear the ref on error to prevent stale state
      localUserMessageRef.current = null;
    }
  };

  const handleSelectConversation = async (convId: string) => {
    // If selecting the already-selected conversation, collapse the sidebar and restore scroll
    if (convId === conversationId) {
      setIsConversationSidebarExpanded(false);
      // Ensure messages are visible
      // Restore scroll position after a brief delay to ensure DOM is ready
      setTimeout(() => {
        if (scrollAreaRef.current && scrollPositionsRef.current.has(convId)) {
          scrollAreaRef.current.scrollTop = scrollPositionsRef.current.get(convId) || 0;
        }
      }, 0);
      return;
    }

    // Clear current messages and closed blocks tracking
    // Clear current messages and closed blocks tracking
    setMessages([]);
    setBlockStore({
      blocks: new Map(),
      pendingToolCalls: new Map()
    });
    closedBlocksRef.current.clear();
    processedEventIdsRef.current.clear(); // Clear processed events for new conversation
    // Reset auto-scrolling when switching conversations
    isAutoScrollingRef.current = true;
    // Reset agent states and input when switching conversations
    setIsAgentRunning(false);
    setIsInterrupting(false);
    setIsInputDisabled(false); // Enable input for the new conversation
    // Reset token usage when switching conversations
    setTokenUsage(null);

    // Find the selected conversation and update the model if it has one
    const selectedConv = conversations.find(c => c.id === convId);
    if (selectedConv?.active_model) {
      setSelectedModel(selectedConv.active_model);
    }

    // Clear the local user message ref when switching conversations
    // This prevents skipping user messages from the previous conversation
    localUserMessageRef.current = null;

    // Set the conversation ID - the useEffect hook will handle connecting to the listener
    // which will automatically load the conversation history
    setConversationId(convId);
    setIsConversationSidebarExpanded(false);
  };

  const reloadIframe = () => {
    if (iframeRef.current && application) {
      setIsIframeLoading(true);
      // Add a cache-busting parameter to force reload
      const url = new URL(application.preview_url);
      url.searchParams.set('_refresh', Date.now().toString());
      iframeRef.current.src = url.toString();
    }
  };


  const handleDeploy = async () => {
    if (!appId || isDeploying) return;

    setIsDeploying(true);

    try {
      const token = await getToken();
      const response = await fetch(`/api/applications/${appId}/deploy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Deployment failed:', error);
        toast.error(`Deployment failed: ${error.error || 'Unknown error'}`);
      } else {
        // Refresh application data to get updated prod_url
        const appResponse = await fetch(`/api/applications/${appId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (appResponse.ok) {
          const data = await appResponse.json();
          setApplication(data);

          // Show success toast with link to production site
          const isRedeploy = application.prod_url ? true : false;
          const prodUrl = data.prod_url || application.prod_url;

          toast.custom((t) => (
            <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
              <div className="flex-1 w-0 p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 pt-0.5">
                    <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                      <Rocket className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {isRedeploy ? 'Redeployment' : 'Deployment'} initiated successfully!
                    </p>
                    {prodUrl && (
                      <p className="mt-1 text-sm text-gray-500">
                        Your app will be live in a few moments
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {prodUrl && (
                <div className="flex border-l border-gray-200">
                  <a
                    href={prodUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => toast.dismiss(t.id)}
                    className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-green-600 hover:text-green-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    View Site
                  </a>
                </div>
              )}
            </div>
          ), {
            duration: 6000,
            position: 'top-right',
          });
        }
      }
    } catch (error) {
      console.error('Failed to deploy:', error);
      toast.error('Failed to initiate deployment');
    } finally {
      setIsDeploying(false);
    }
  };

  const handleInterrupt = async () => {
    if (!isAgentRunning || isInterrupting || !conversationId || !appId) return;

    setIsInterrupting(true);

    try {
      const token = await getToken();
      const response = await fetch(`/api/demo/applications/${appId}/interrupt?conversation_id=${conversationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error('Failed to interrupt agent');
        // Reset interrupting state on error
        setIsInterrupting(false);
      }
      // If successful, the finish event will reset the states
    } catch (error) {
      console.error('Failed to interrupt:', error);
      setIsInterrupting(false);
    }
  };

  const handleTryAgain = async () => {
    if (!conversationId || !appId || isAgentRunning) return;

    // Re-enable input and set agent as running
    setIsInputDisabled(true);
    setIsAgentRunning(true);

    try {
      const token = await getToken();
      const response = await fetch(`/api/demo/applications/${appId}/continue?conversation_id=${conversationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error('Failed to continue conversation');
        // Reset states on error
        setIsInputDisabled(false);
        setIsAgentRunning(false);
      }
      // If successful, the agent will start sending events through the SSE connection
      // and the finish event will reset the states
    } catch (error) {
      console.error('Failed to continue:', error);
      setIsInputDisabled(false);
      setIsAgentRunning(false);
    }
  };

  const handleNewConversation = () => {
    setMessages([]);
    setBlockStore({
      blocks: new Map(),
      pendingToolCalls: new Map()
    });
    processedEventIdsRef.current.clear(); // Clear processed events for new conversation
    closedBlocksRef.current.clear();
    // Clear the local user message ref when starting a new conversation
    localUserMessageRef.current = null;
    setConversationId('');
    // Reset auto-scrolling when starting a new conversation
    isAutoScrollingRef.current = true;
    // Reset agent states and input for new conversation
    setIsAgentRunning(false);
    setIsInterrupting(false);
    setIsInputDisabled(false); // Enable input for new conversation
    // Reset token usage for new conversation
    setTokenUsage(null);
    // Refresh conversations list
    const fetchConversations = async () => {
      if (!appId) return;

      try {
        const token = await getToken();
        const response = await fetch(`/api/demo/applications/${appId}/conversations`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setConversations(data);
        }
      } catch (error) {
        console.error('Failed to fetch conversations:', error);
      }
    };

    fetchConversations();
  };

  return (
    <div className="h-full flex overflow-hidden">
      {/* Conversation Sidebar */}
      <div
        className={`flex-shrink-0 transition-all duration-300 bg-gray-50 border-r border-gray-200 ${
          isConversationSidebarExpanded ? 'w-64' : 'w-12'
        }`}
      >
        {isConversationSidebarExpanded ? (
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
              <span className="text-sm font-semibold text-gray-700">Conversations</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsConversationSidebarExpanded(false)}
                className="h-8 w-8"
                title="Hide conversations"
                aria-label="Hide conversations"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-hidden">
              <ConversationSidebar
                conversations={conversations}
                selectedConversationId={conversationId}
                onSelectConversation={handleSelectConversation}
                onNewConversation={handleNewConversation}
                loading={loadingConversations}
                className="bg-transparent border-0"
              />
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsConversationSidebarExpanded(true)}
              className="h-10 w-10"
              title="Show conversations"
              aria-label="Show conversations"
            >
              <MessageSquare className="h-5 w-5" />
            </Button>
          </div>
        )}
      </div>

      {/* Chat Interface */}
      <div className="flex flex-col min-w-0 flex-1">
        <Card className="flex-1 flex flex-col min-h-0 min-w-0 max-h-full shadow-none border-0 rounded-none">
          <CardContent className="flex-1 flex flex-col p-0 min-h-0 overflow-hidden border-r border-gray-200 rounded-none">
            <div
              ref={scrollAreaRef}
              className="flex-1 overflow-auto p-4"
              onScroll={handleScroll}
              style={{
                opacity: isMessagesVisible ? 1 : 0,
                transition: 'opacity 0.05s ease-in'
              }}
            >
              <MessageList messages={messages} blockStore={blockStore} onTryAgain={handleTryAgain} applicationId={appId} onSendMessage={sendMessage} />
              {/* Staged messages */}
              {stagedMessages.length > 0 && (
                <div className="mt-4 space-y-2">
                  {stagedMessages.map((msg, index) => (
                    <div key={index} className="text-gray-400 text-sm">
                      Queued: {msg}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Thinking indicator - separate fixed section */}
            {isAgentRunning && (
              <div className="px-4 py-2 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="inline-block animate-pulse" style={{ color: 'rgb(109, 76, 153)' }}>○</span>
                  <span className="text-sm">
                    <VibeText />
                    <span className="ml-2 text-xs text-gray-400">
                      {isInterrupting ? '(interrupting...)' : '(esc to interrupt)'}
                    </span>
                  </span>
                </div>
              </div>
            )}
            <div className="border-t p-2 flex-shrink-0">
              <MessageInput
                onSendMessage={sendMessage}
                disabled={isInputDisabled && !isAgentRunning}
                placeholder={isAgentRunning ? "Stage a message for the agent..." : "Type a message..."}
                models={models}
                selectedModel={selectedModel}
                onModelChange={(model) => {
                  setSelectedModel(model);
                  localStorage.setItem('selectedModel', model);
                }}
                tokenUsage={tokenUsage}
                stagedMessages={stagedMessages}
                failedMessages={failedStagedMessages}
                onClearFailedMessages={() => setFailedStagedMessages('')}
                onFocus={() => setIsSidebarExpanded(false)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right Panel - Preview/Iframe (fixed width) */}
      <div
        className={`flex flex-col min-w-0 transition-all duration-300 ${
          isSidebarExpanded ? 'w-[500px]' : 'w-[350px]'
        }`}
      >
        <Card className="flex-1 flex flex-col min-h-0 min-w-0 max-h-full shadow-none border-0 rounded-none">
          <CardContent className="flex-1 flex flex-col p-0 min-h-0 overflow-hidden rounded-none">
            {/* Browser navbar */}
            {application && application.preview_url && (
              <div className="border-b bg-gray-50 px-3 py-2 flex items-center gap-4 flex-shrink-0 min-w-0 overflow-x-auto">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={reloadIframe}
                    disabled={isIframeLoading}
                    className="h-8 w-8 flex-shrink-0"
                    title="Refresh"
                  >
                    <RefreshCw className={`h-4 w-4 ${isIframeLoading ? 'animate-spin' : ''}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsVaultModalOpen(true)}
                    className="h-8 w-8 flex-shrink-0"
                    title="Vault Secrets"
                  >
                    <Lock className="h-4 w-4" />
                  </Button>

                  {/* Environment Links */}
                  <div className="flex flex-col gap-1 ml-2 border-l pl-2">
                    <a
                      href={application.preview_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-600 hover:text-blue-700 text-xs font-medium transition-colors duration-200"
                      title="Open preview environment"
                    >
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 hover:bg-blue-100 rounded transition-colors">
                        Preview
                        <ExternalLink className="h-2.5 w-2.5" />
                      </span>
                    </a>
                    {application.prod_url && (
                      <a
                        href={application.prod_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-green-600 hover:text-green-700 text-xs font-medium transition-colors duration-200"
                        title="Open production environment"
                      >
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-50 hover:bg-green-100 rounded transition-colors">
                          Production
                          <ExternalLink className="h-2.5 w-2.5" />
                        </span>
                      </a>
                    )}
                  </div>
                </div>

                {/* Deploy Section */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isDeploying ? (
                    <div className="h-8 px-3 flex items-center gap-2 bg-green-50 text-green-600 rounded-md">
                      <Rocket className="h-4 w-4 animate-pulse" />
                      <span className="text-sm font-medium whitespace-nowrap">
                        Deploying...
                      </span>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={handleDeploy}
                      className="h-8 px-3 flex items-center gap-2 flex-shrink-0"
                      title={application.prod_url ? 'Redeploy to Production' : 'Deploy to Production'}
                    >
                      <Rocket className="h-4 w-4" />
                      <span className="text-sm font-medium whitespace-nowrap">
                        {application.prod_url ? 'Redeploy' : 'Deploy'}
                      </span>
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Iframe content area */}
            <div
              className={`relative bg-gray-100 overflow-auto min-h-0 transition-all duration-300 ${isErrorsExpanded ? 'flex-shrink-0 h-0' : 'flex-1'}`}
            >
              {isIframeLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                    <span className="mt-2 text-sm text-gray-600">Loading preview...</span>
                  </div>
                </div>
              )}
              {application && application.preview_url && (
                <div className="w-full h-full p-4">
                  <div
                    ref={iframeContainerRef}
                    className="w-full h-full flex items-start justify-center overflow-auto"
                  >
                    <div
                      className="relative"
                      style={{
                        width: scaledPreviewWidth,
                        height: scaledPreviewHeight,
                        cursor: isSidebarExpanded ? 'default' : 'pointer',
                      }}
                      onClick={() => {
                        if (!isSidebarExpanded) {
                          setIsSidebarExpanded(true);
                        }
                        // Collapse errors when clicking preview
                        if (isErrorsExpanded) {
                          setIsErrorsExpanded(false);
                          setSelectedErrorId(null);
                        }
                      }}
                    >
                      <div
                        className="relative bg-white shadow-lg"
                        style={{
                          width: PREVIEW_BASE_WIDTH,
                          height: PREVIEW_BASE_HEIGHT,
                          transform: `scale(${iframeScale})`,
                          transformOrigin: 'top left',
                        }}
                      >
                        <iframe
                          ref={iframeRef}
                          src={application.preview_url}
                          className="w-full h-full border-0"
                          style={{
                            pointerEvents: isSidebarExpanded ? 'auto' : 'none',
                          }}
                          title={`Preview of ${application.name}`}
                          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-popups-to-escape-sandbox allow-storage-access-by-user-activation allow-downloads"
                          onLoad={() => setIsIframeLoading(false)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {!application && (
                <div className="h-full flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No preview available</p>
                  </div>
                </div>
              )}
            </div>

            {/* Error list section */}
            {appId && (
              <div className={`transition-all duration-300 ${isErrorsExpanded ? "flex-1 overflow-hidden" : "flex-shrink-0"}`}>
                <ErrorListMinimal
                  appId={appId}
                  onAskAgentToFix={handleAskAgentToFix}
                  onAskAgentToFixError={handleAskAgentToFixError}
                  isExpanded={isErrorsExpanded}
                  selectedErrorId={selectedErrorId}
                  onErrorClick={handleErrorClick}
                  onClose={handleCloseErrorDetails}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Vault Modal */}
      {appId && (
        <VaultModal
          isOpen={isVaultModalOpen}
          onClose={() => setIsVaultModalOpen(false)}
          appId={appId}
        />
      )}

      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        reverseOrder={false}
        toastOptions={{
          duration: 5000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          error: {
            style: {
              background: '#ef4444',
              color: '#fff',
            },
          },
        }}
      />
    </div>
  );
};
